import React from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

/** Floating glass surface for controls that sit on top of the 3D canvas. */
export const Glass = ({ className, children, testId, ...rest }) => (
  <div className={cn("pointer-events-auto rounded-md bg-background/90 shadow-pop backdrop-blur-md", className)} data-testid={testId} {...rest}>
    {children}
  </div>
);

/** Segmented control – one active value, quiet inactive options. */
export const Seg = ({ value, options, onChange, testId, size = "md", className, prefix, disabled }) => (
  <div className={cn("inline-flex items-stretch rounded-md p-0.5", size === "sm" ? "h-7" : "h-8", className)} role="group" data-testid={testId}>
    {prefix && <span className="self-center pl-2 pr-1 text-[11.5px] text-muted-foreground">{prefix}</span>}
    {options.map((o) => (
      <button
        key={String(o.value)}
        type="button"
        onClick={() => onChange(o.value)}
        aria-pressed={value === o.value}
        disabled={disabled || o.disabled}
        title={o.title}
        className={cn(
          "min-w-[1.9rem] rounded-[4px] px-2 text-[12.5px] font-medium tabular transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-40",
          value === o.value ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
        data-testid={testId ? `${testId}-${o.value}` : undefined}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/** Icon-only tool button with tooltip + optional shortcut hint. */
export const ToolButton = ({ icon: Icon, label, onClick, disabled, testId, active, danger, side = "bottom", className, shortcut }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={active}
        data-testid={testId}
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-[4px] text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-35",
          active && "bg-foreground text-background hover:bg-foreground hover:text-background",
          danger && "hover:text-destructive",
          className
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </TooltipTrigger>
    <TooltipContent side={side} className="flex items-center gap-2 text-[12px]">
      {label}
      {shortcut && <kbd className="kbd">{shortcut}</kbd>}
    </TooltipContent>
  </Tooltip>
);

/** Small status dot for validation / delivery states. */
export const Dot = ({ color, className, ring = true }) => (
  <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", ring && "ring-1 ring-black/10", className)} style={color ? { background: color } : undefined} aria-hidden />
);

export const LEVEL_DOT = { ok: "bg-emerald-500", warn: "bg-amber-500", error: "bg-rose-500", info: "bg-slate-400" };
